// Remarques de l'équipe sur un ticket : texte et captures, lus par l'agent à
// chaque passe (et le texte par la revue croisée). On les ajoute quand le
// travail ne convient pas tout à fait, puis on relance.
import type { JSX } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import type { TowerNote, TowerTicketDetail } from '@feedbaacks/sdk'
import { Button, IconButton, Icons, errorMessage, useFeedbaacks } from '@feedbaacks/ui'
import { shortTime } from './tower-model'
import { Section } from './TowerParts'

const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif'
const MAX_IMAGES = 5
const MAX_BYTES = 10 * 1024 * 1024

/** Une capture du ticket, lue avec le jeton de session (pas de cookie). */
function NoteImage({ ticketId, mediaId, width, height }: { ticketId: string; mediaId: string; width: number; height: number }): JSX.Element {
  const { client } = useFeedbaacks()
  const [url, setUrl] = useState('')
  useEffect(() => {
    let alive = true
    let made = ''
    client.tower.noteMedia(ticketId, mediaId).then((b) => {
      made = URL.createObjectURL(b)
      if (alive) setUrl(made)
      else URL.revokeObjectURL(made)
    }, () => {})
    return () => {
      alive = false
      if (made) URL.revokeObjectURL(made)
    }
  }, [ticketId, mediaId])
  return (
    <a class="tw-note-thumb" href={url || undefined} target="_blank" rel="noreferrer" title={`${width}×${height}`}>
      {url ? <img src={url} alt="Capture jointe" width={width} height={height} /> : <span class="fb-muted">…</span>}
    </a>
  )
}

function NoteItem({ ticketId, note }: { ticketId: string; note: TowerNote }): JSX.Element {
  return (
    <li class="tw-note">
      <div class="tw-note-head fb-muted">
        <span>{note.author || 'Équipe'}</span>
        <time dateTime={note.at} title={new Date(note.at).toLocaleString('fr-FR')}>{shortTime(note.at)}</time>
      </div>
      {note.body && <p class="tw-note-body">{note.body}</p>}
      {note.media.length > 0 && (
        <div class="tw-note-media">
          {note.media.map((m) => <NoteImage key={m.id} ticketId={ticketId} mediaId={m.id} width={m.width} height={m.height} />)}
        </div>
      )}
    </li>
  )
}

/** Fichiers retenus : images seulement, cinq au plus, 10 Mo chacune. */
export function pickImages(current: File[], added: File[]): { files: File[]; refused: number } {
  const ok = added.filter((f) => IMAGE_ACCEPT.split(',').includes(f.type) && f.size <= MAX_BYTES)
  const files = [...current, ...ok].slice(0, MAX_IMAGES)
  return { files, refused: added.length - (files.length - current.length) }
}

export function TowerNotes({ ticket, onChanged, relaunch }: {
  ticket: TowerTicketDetail
  onChanged: () => void
  /** Ticket livré : « Relancer » vit ici (pas de bandeau d'échec). */
  relaunch?: { busy: boolean; onClick: () => void }
}): JSX.Element {
  const { client, notify } = useFeedbaacks()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f))
    setPreviews(urls)
    return () => urls.forEach((u) => URL.revokeObjectURL(u))
  }, [files])

  const add = (list: FileList | File[] | null | undefined) => {
    if (!list) return
    const r = pickImages(files, Array.from(list))
    setFiles(r.files)
    if (r.refused > 0) notify('error', `${r.refused} fichier${r.refused > 1 ? 's' : ''} écarté${r.refused > 1 ? 's' : ''} : images seulement, ${MAX_IMAGES} au plus, 10 Mo chacune.`)
  }
  const onPaste = (e: ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData?.files ?? [])
    if (pasted.length) {
      e.preventDefault()
      add(pasted)
    }
  }
  const submit = async (e: Event) => {
    e.preventDefault()
    if (!text.trim() && files.length === 0) return
    setBusy(true)
    try {
      await client.tower.addNote(ticket.id, text.trim(), files)
      setText('')
      setFiles([])
      notify('success', 'Remarque ajoutée : l\'agent la lira à sa prochaine passe.')
    } catch (err) {
      notify('error', `Remarque refusée : ${errorMessage(err, 'erreur inattendue')}`)
    } finally {
      setBusy(false)
      onChanged()
    }
  }

  const empty = !text.trim() && files.length === 0
  return (
    <Section title="Remarques pour l'agent" aside={ticket.notes.length ? `${ticket.notes.length}` : undefined}>
      <p class="fb-muted tw-small">
        Lues par l'agent à chaque passe, captures comprises, et par la revue croisée. Ajoutez-les, puis relancez : l'agent reprend sa branche.
      </p>
      {ticket.notes.length > 0 && (
        <ol class="tw-notes" data-testid="tower-notes">
          {ticket.notes.map((n) => <NoteItem key={n.id} ticketId={ticket.id} note={n} />)}
        </ol>
      )}
      <form class="tw-note-form" onSubmit={submit}>
        <textarea class="fb-input" rows={3} value={text} placeholder="Ce qui ne va pas, ce qui est attendu… (Ctrl+V pour coller une capture)"
          onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} onPaste={onPaste} disabled={busy} data-testid="tower-note-text" />
        {previews.length > 0 && (
          <div class="tw-note-media">
            {previews.map((u, i) => (
              <span key={u} class="tw-note-thumb is-pending">
                <img src={u} alt={files[i]?.name ?? 'Capture'} />
                <IconButton label="Retirer" onClick={() => setFiles(files.filter((_, j) => j !== i))}><Icons.X size={12} /></IconButton>
              </span>
            ))}
          </div>
        )}
        <div class="tw-row">
          <input ref={input} type="file" accept={IMAGE_ACCEPT} multiple hidden data-testid="tower-note-files"
            onChange={(e) => { add((e.target as HTMLInputElement).files); (e.target as HTMLInputElement).value = '' }} />
          <Button onClick={() => input.current?.click()} disabled={busy || files.length >= MAX_IMAGES}><Icons.Camera size={14} />Captures</Button>
          <span class="tw-push" />
          <Button type="submit" variant="primary" disabled={busy || empty} testId="tower-note-submit"><Icons.Send size={14} />Ajouter</Button>
        </div>
      </form>
      {relaunch && (
        <div class="tw-banner is-info" data-testid="tower-relaunch-delivered">
          <span>Livré, mais pas tout à fait comme attendu ? Ajoutez vos remarques, puis relancez : l'agent repart de {ticket.default_branch} et une nouvelle PR suivra.</span>
          <Button disabled={relaunch.busy || busy} onClick={relaunch.onClick} testId="tower-relaunch-button"><Icons.Refresh size={14} />Relancer</Button>
        </div>
      )}
    </Section>
  )
}
